// Personal records. The same code flags a PR on the phone the moment a set is logged (against the bests it saved
// for offline use) and works out records on the server, so both always agree. Weights are compared in kg,
// rounded to 10 g, so 135 lb is always equal to 135 lb whatever unit it went through. Warm-ups never count.
import { z } from 'zod';
import { estimateOneRepMax } from './e1rm.ts';
import type { ExerciseType } from './training.ts';
import { convertWeight } from './units.ts';
import type { SetRecord } from './workouts.ts';

export const Bests = z.object({
  /** Heaviest weight lifted for at least one rep. */
  weightKg: z.number().nullable(),
  /** Highest estimated one-rep max. */
  e1rmKg: z.number().nullable(),
  /** Most reps at each weight, heaviest first, keeping only weights where it beats every heavier one. */
  repsAtWeight: z.array(z.tuple([z.number(), z.number()])),
  /** Most reps in one set (bodyweight exercises). */
  reps: z.number().nullable(),
  /** Longest hold or cardio session, in seconds. */
  durationS: z.number().nullable(),
  /** Longest cardio distance, in metres. */
  distanceM: z.number().nullable(),
});
export type Bests = z.infer<typeof Bests>;

export const PrKind = z.enum(['weight', 'e1rm', 'reps', 'duration', 'distance']);
export type PrKind = z.infer<typeof PrKind>;
export const PR_NAMES: Record<PrKind, string> = { weight: 'Heaviest', e1rm: 'Best est. 1RM', reps: 'Most reps', duration: 'Longest', distance: 'Farthest' };

export const EMPTY_BESTS: Bests = { weightKg: null, e1rmKg: null, repsAtWeight: [], reps: null, durationS: null, distanceM: null };

const kg = (s: Pick<SetRecord, 'weight' | 'unit'>) => (s.weight === null ? null : Math.round(convertWeight(s.weight, s.unit, 'kg') * 100) / 100);
const counts = (s: SetRecord) => !s.warmup && !s.deletedAt;
const more = (value: number | null, best: number | null) => value !== null && value > 0 && (best === null || value > best);

/** Which records this set beats. Nothing counts the very first time (every set would be a "record"). */
export function prsFor(type: ExerciseType, set: SetRecord, before: Bests): PrKind[] {
  if (!counts(set)) return [];
  const out: PrKind[] = [];
  if (type === 'weight_reps') {
    const w = kg(set);
    const reps = set.reps ?? 0;
    if (w === null || reps < 1 || before.weightKg === null) return [];
    if (w > before.weightKg) out.push('weight');
    const e1rm = round2(estimateOneRepMax(w, reps));
    if (before.e1rmKg !== null && e1rm > before.e1rmKg) out.push('e1rm');
    // More reps than ever at this weight or heavier, at a weight you've lifted before.
    if (w <= before.weightKg && !before.repsAtWeight.some(([bw, br]) => bw >= w && br >= reps)) out.push('reps');
  } else if (type === 'bodyweight_reps') {
    if (before.reps !== null && more(set.reps, before.reps)) out.push('reps');
  } else {
    if (before.durationS !== null && more(set.durationS, before.durationS)) out.push('duration');
    if (type === 'cardio' && before.distanceM !== null && more(set.distanceM, before.distanceM)) out.push('distance');
  }
  return out;
}

/** Bests after adding one set. */
export function addToBests(type: ExerciseType, b: Bests, set: SetRecord): Bests {
  if (!counts(set)) return b;
  if (type === 'weight_reps') {
    const w = kg(set);
    const reps = set.reps ?? 0;
    if (w === null || reps < 1) return b;
    const e1rm = round2(estimateOneRepMax(w, reps));
    return {
      ...b,
      weightKg: Math.max(b.weightKg ?? 0, w),
      e1rmKg: Math.max(b.e1rmKg ?? 0, e1rm),
      repsAtWeight: frontier([...b.repsAtWeight, [w, reps]]),
    };
  }
  if (type === 'bodyweight_reps') return set.reps ? { ...b, reps: Math.max(b.reps ?? 0, set.reps) } : b;
  return {
    ...b,
    durationS: set.durationS ? Math.max(b.durationS ?? 0, set.durationS) : b.durationS,
    distanceM: type === 'cardio' && set.distanceM ? Math.max(b.distanceM ?? 0, set.distanceM) : b.distanceM,
  };
}

/** Bests over a list of sets (any order), optionally on top of earlier bests. */
export const bestsOf = (type: ExerciseType, sets: SetRecord[], start: Bests = EMPTY_BESTS) => sets.reduce((b, s) => addToBests(type, b, s), start);

/** PR flags for each set in logging order, each judged against everything before it. */
export function markPrs(type: ExerciseType, sets: SetRecord[], start: Bests = EMPTY_BESTS): Map<string, PrKind[]> {
  const out = new Map<string, PrKind[]>();
  let b = start;
  for (const s of [...sets].sort((x, y) => x.loggedAt - y.loggedAt)) {
    const prs = prsFor(type, s, b);
    if (prs.length) out.set(s.id, prs);
    b = addToBests(type, b, s);
  }
  return out;
}

/** Keep (weight, reps) pairs no heavier-or-equal pair beats on reps; heaviest first. */
function frontier(points: [number, number][]): [number, number][] {
  const sorted = [...points].sort((a, b) => b[0] - a[0] || b[1] - a[1]);
  const out: [number, number][] = [];
  for (const [w, r] of sorted) if (!out.length || r > out.at(-1)![1]) out.push([w, r]);
  return out;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
